
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Loader2 } from 'lucide-react';
import { generateLegalDocument } from '@/services/apiService';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import VoiceInput from './VoiceInput';
import TextToSpeech from './TextToSpeech';

interface GeneratorFormProps {
  onResult: (result: string) => void;
}

const GeneratorForm: React.FC<GeneratorFormProps> = ({ onResult }) => {
  const [userInput, setUserInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [generatedText, setGeneratedText] = useState<string | null>(null);
  const { language, t } = useLanguage();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!userInput.trim()) {
      toast.error(language === 'ur' ? 'براہ کرم اپنا مسئلہ بیان کریں' : 'Please describe your issue');
      return;
    }
    
    setIsLoading(true);
    try {
      const result = await generateLegalDocument(userInput, language);
      
      if (result.success) {
        setGeneratedText(result.content);
        onResult(result.content);
        toast.success(language === 'ur' ? 'دستاویز تیار کی گئی' : 'Document generated successfully');
      }
    } catch (error) {
      console.error("Error generating document:", error);
      toast.error(language === 'ur' ? 'دستاویز تیار کرنے میں خرابی' : 'Error generating document');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVoiceInput = (text: string) => {
    setUserInput(text);
  };

  return (
    <form onSubmit={handleSubmit} className="max-w-2xl mx-auto px-4">
      <div className="adalat-card">
        <div className={`flex justify-between items-center mb-2 ${language === 'ur' ? 'flex-row-reverse' : ''}`}>
          <label 
            htmlFor="userInput" 
            className={`block ${language === 'ur' ? 'text-urdu-lg font-urdu text-right' : 'text-lg font-medium'}`}
          >
            {language === 'ur' ? 'اپنا مسئلہ بیان کریں' : 'Describe your issue'}
          </label>
          <VoiceInput onTranscript={handleVoiceInput} />
        </div>
        <Textarea
          id="userInput"
          dir={language === 'ur' ? 'rtl' : 'ltr'}
          placeholder={language === 'ur' ? 'یہاں تفصیلات درج کریں...' : 'Enter details here...'}
          value={userInput}
          onChange={(e) => setUserInput(e.target.value)}
          className={`min-h-[150px] mb-4 text-lg border-blue-200 focus:border-blue-300 focus:ring-blue-300 ${
            language === 'ur' ? 'text-right font-urdu text-urdu-lg' : 'text-left font-english'
          }`}
          disabled={isLoading}
        />
        <div className="flex justify-between items-center mt-4">
          <Button 
            type="submit" 
            className="adalat-button px-8 py-3 text-white" 
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className={`animate-spin-slow ${language === 'ur' ? "ml-2" : "mr-2"}`} size={16} />
                {language === 'ur' ? 'تیار کر رہا ہے...' : 'Generating...'}
              </>
            ) : (
              language === 'ur' ? 'دستاویز تیار کریں' : 'Generate Document'
            )}
          </Button>
          
          {generatedText && (
            <TextToSpeech text={generatedText} />
          )}
        </div>
      </div>
    </form>
  );
};

export default GeneratorForm;
